<template>
  <section
    class="pass-history-view v3-pass-history behaviour-student-list-tab"
    :aria-label="behaviourTerminology.studentListTab"
    data-testid="behaviour-student-list-tab"
  >
    <BehaviourStudentPointHistoryTab
      v-if="selectedStudent"
      :student="selectedStudent"
      hide-page-header
      hide-staff-actions
    />

    <template v-else>
      <h2 class="sr-only">{{ behaviourTerminology.studentListTab }}</h2>

      <ActionPanel class="mb-3 behaviour-point-history-action-panel">
        <template #left-content>
          <div
            class="d-flex flex-wrap align-items-end gap-3 behaviour-point-history-toolbar w-100"
          >
            <div
              class="behaviour-point-history-search-field min-w-0 flex-grow-1"
            >
              <label
                id="behaviour-student-list-search-label"
                class="v3-form-label d-block mb-1"
                for="behaviour-student-list-search"
              >
                {{ $t("common.search") }}
              </label>
              <InputField
                class="behaviour-point-history-search"
                prepend-icon="ri-search-line"
                :model-value="state.searchInput"
                :placeholder="$t('common.search')"
                input-type="search"
                id="behaviour-student-list-search"
                data-testid="behaviour-student-list-search"
                @update:model-value="onSearchInput"
              />
            </div>
            <div class="behaviour-point-history-grad-year-field min-w-0">
              <label
                id="behaviour-student-list-grad-year-label"
                class="v3-form-label d-block mb-1"
                for="behaviour-student-list-grad-year-input"
              >
                {{ $t("common.grad_year") }}
              </label>
              <CustomSelect
                v-if="state.gradYears"
                v-model="state.filterParams.byGradYear"
                class="behaviour-point-history-grad-year-filter w-100"
                hide-floating-label
                labelled-by="behaviour-student-list-grad-year-label"
                :close-on-select="true"
                :multiple="true"
                :options="state.gradYears"
                :searchable="false"
                :placeholder="$t('common.by_grad_year')"
                input-id="behaviour-student-list-grad-year-input"
                data-testid="behaviour-student-list-grad-year-filter"
                @update:model-value="onGradYearChange"
              />
            </div>
          </div>
        </template>
        <template #right-content>
          <div
            class="d-flex align-items-end flex-wrap justify-content-end gap-2 behaviour-point-history-filter-actions"
          >
            <button
              type="button"
              class="btn btn-link d-flex align-items-center p-0 text-primary text-decoration-none"
              :disabled="!hasActiveFilters || state.isLoading"
              data-testid="behaviour-student-list-reset-filters"
              @click="resetFilters"
            >
              <i class="ri-filter-off-line" aria-hidden="true" />
              <span class="ms-1 text-decoration-underline">{{
                $t("behaviour_settings.reset_filters")
              }}</span>
            </button>
            <IconButton
              class="h-25"
              :icon="
                state.isExportLoading
                  ? 'ri-loader-4-line'
                  : 'ri-download-2-line'
              "
              :class="{ loading: state.isExportLoading }"
              :disabled="state.isExportLoading || state.isLoading"
              :aria-label="$t('common.download_export')"
              data-testid="behaviour-student-list-download"
              @click="downloadStudentList"
            />
          </div>
        </template>
      </ActionPanel>

      <Loader :is-processing="state.isLoading" />

      <PerPage
        v-if="state.pagination.total > 0"
        :pagination="paginationDisplay"
        :showing-entries="true"
        @on-page-change="setPerPage"
      />

      <DataTable
        class="datatable bg-white text-center mt-3"
        add-table-classes="behaviour-student-list-datatable"
        :fields="tableFields"
        :items="state.tableRows"
        :sorter="{ external: true, resetable: false }"
        :sorter-value="sorterValue"
        :no-items-view="noItemsView"
        :keyboard-scrollable-region="true"
        :scroll-region-label="
          $t('behaviour_manage.table_scroll_region', {
            name: behaviourTerminology.studentListTab
          })
        "
        data-testid="behaviour-student-list-table"
        @update:sorter-value="onSortColumnChange"
      >
        <template #studentName="{ item }">
          <td class="text-start">
            <button
              type="button"
              class="btn btn-link p-0 text-primary text-decoration-underline behaviour-student-list-name-link"
              :data-testid="'behaviour-student-list-name-' + item.studentId"
              :aria-label="
                $t('behaviour_manage.view_student_point_history', {
                  name: item.studentName || item.studentNumber,
                  point: behaviourTerminology.point
                })
              "
              @click="openStudentPointHistory(item)"
            >
              {{ item.studentName || "—" }}
            </button>
          </td>
        </template>
        <template #studentNumber="{ item }">
          <td class="text-start">{{ item.studentNumber || "—" }}</td>
        </template>
        <template #email="{ item }">
          <td class="text-start">{{ item.email || "—" }}</td>
        </template>
        <template #gradYear="{ item }">
          <td class="text-start">{{ formatGradYear(item.gradYear) }}</td>
        </template>
        <template #totalPoints="{ item }">
          <td class="text-start">{{ item.totalPoints }}</td>
        </template>
      </DataTable>

      <div
        v-if="state.isLazyLoadingMode && canLoadMoreLazy"
        ref="lazySentinelRef"
        class="behaviour-core-value-lazy-sentinel py-2 text-center text-muted small"
        data-testid="behaviour-student-list-lazy-sentinel"
        aria-hidden="true"
      >
        {{ $t("behaviour_settings.lazy_scroll_hint") }}
      </div>

      <Pagination
        v-if="paginationDisplay.pages > 1 && !state.isLazyLoadingMode"
        :active-page="state.pagination.activePage"
        :pages="paginationDisplay.pages"
        class="cs-pagination mt-4"
        :dots="false"
        :double-arrows="true"
        size="lg"
        align="center"
        @update:active-page="setActivePage"
      />
    </template>
  </section>
</template>

<script>
import { useBehaviourTableLazyScroll } from "@/composables/useBehaviourTableLazyScroll.js"
import { usePbisPointsSocketRefresh } from "@/composables/usePbisPointsSocketRefresh.js"
import download from "@/helpers/downloadCSV.js"
import { mapStudentListSortKey } from "@/helpers/behaviourStudentListApi.js"
import {
  applyBehaviourTableFetchResult,
  resolveBehaviourTableLazyRequest
} from "@/helpers/behaviourTableLazyLoading.js"
import BehaviourStudentPointHistoryTab from "@/v3views/BehaviourStudentPointHistoryTab.vue"
import ActionPanel from "@/v3components/shared/DataTable/ActionPanel.vue"
import IconButton from "@/v3components/shared/Buttons/IconButton.vue"
import DataTable from "@/v3components/shared/DataTable/DataTable.vue"
import InputField from "@/v3components/shared/Form/InputField.vue"
import Loader from "@/v3components/shared/Loader/Loader.vue"
import CustomSelect from "@/v3components/shared/Form/CustomSelect.vue"
import Pagination from "@/v3components/shared/DataTable/Pagination.vue"
import PerPage from "@/v3components/shared/DataTable/Perpage.vue"
import {
  computed,
  nextTick,
  onMounted,
  onUnmounted,
  reactive,
  watch
} from "vue"
import { useI18n } from "vue-i18n"
import { useStore } from "vuex"

export default {
  name: "BehaviourStudentListTab",
  props: {
    selectedStudent: {
      type: Object,
      default: null
    }
  },
  emits: ["select-student", "clear-student"],
  components: {
    ActionPanel,
    IconButton,
    BehaviourStudentPointHistoryTab,
    CustomSelect,
    DataTable,
    InputField,
    Loader,
    Pagination,
    PerPage
  },
  setup(props, { emit }) {
    const SEARCH_DEBOUNCE_MS = 320
    const { t } = useI18n()
    const store = useStore()

    const behaviourTerminology = computed(
      () => store.getters["behaviourSettings/behaviourTerminology"]
    )

    let searchDebounceId = null

    const state = reactive({
      isLoading: false,
      isLazyAppendInFlight: false,
      isLazyLoadingMode: false,
      lazyLoadedCount: 0,
      isExportLoading: false,
      searchInput: "",
      gradYears: [],
      tableRows: [],
      filterParams: {
        byGradYear: []
      },
      sortColumn: null,
      sortOrder: null,
      pagination: {
        activePage: 1,
        total: 0,
        pages: 0,
        from: 0,
        to: 0,
        per_page: { label: "25", value: 25 }
      }
    })

    const tableFields = computed(() => [
      {
        key: "studentName",
        label: t("behaviour_manage.col_student_name"),
        sorter: false,
        _style: "width:24%;"
      },
      {
        key: "studentNumber",
        label: t("behaviour_manage.col_student_number"),
        sorter: false,
        _style: "width:18%;"
      },
      {
        key: "email",
        label: t("behaviour_manage.col_user_email"),
        sorter: false,
        _style: "width:34%;"
      },
      {
        key: "gradYear",
        label: t("common.grad_year"),
        sorter: false,
        _style: "width:12%;"
      },
      {
        key: "totalPoints",
        label: t("behaviour_manage.col_remaining_points", {
          point: behaviourTerminology.value.point
        }),
        sorter: true,
        _style: "width:12%;"
      }
    ])

    const sorterValue = computed(() => ({
      column: state.sortColumn || "",
      asc: state.sortOrder !== "desc"
    }))

    const paginationDisplay = computed(() => state.pagination)

    const hasActiveFilters = computed(() => {
      const hasSearch = Boolean(String(state.searchInput ?? "").trim())
      const hasGradYear = Boolean(state.filterParams.byGradYear?.length)
      return hasSearch || hasGradYear
    })

    const noItemsView = computed(() => ({
      noItems: t("behaviour_manage.no_student_list_rows")
    }))

    const formatGradYear = (value) => {
      if (value == null || value === "") return "—"
      return String(value)
    }

    const buildListQuery = () => ({
      page: state.pagination.activePage,
      perPage: state.pagination.per_page?.value || 25,
      search: state.searchInput,
      gradYears: state.filterParams.byGradYear,
      sortBy: mapStudentListSortKey(state.sortColumn),
      sortOrder: state.sortOrder
    })

    const fetchStudentListPage = async ({ page, append = false } = {}) => {
      const perPageVal = Number(state.pagination.per_page?.value) || 25
      const perPageLabel =
        state.pagination.per_page?.label || String(perPageVal)
      const { isLazyMode, requestPerPage } =
        resolveBehaviourTableLazyRequest(perPageVal)
      const requestPage = page ?? state.pagination.activePage ?? 1

      const query = {
        page: requestPage,
        perPage: requestPerPage,
        search: state.searchInput,
        gradYears: state.filterParams.byGradYear,
        sortBy: mapStudentListSortKey(state.sortColumn),
        sortOrder: state.sortOrder
      }

      const { rows, pagination } = await store.dispatch(
        "behaviourManage/fetchStudentList",
        query
      )

      applyBehaviourTableFetchResult({
        state,
        rows: rows || [],
        pagination,
        perPageLabel,
        perPageVal,
        isLazyMode,
        append,
        rowsKey: "tableRows"
      })

      return { rows: rows || [], isLazyMode }
    }

    const fetchStudentList = async ({ append = false } = {}) => {
      try {
        if (append) {
          state.isLazyAppendInFlight = true
        } else {
          state.isLoading = true
        }

        const requestPage = append
          ? (Number(state.pagination.activePage) || 1) + 1
          : Number(state.pagination.activePage) || 1

        const { isLazyMode } = await fetchStudentListPage({
          page: requestPage,
          append
        })

        if (append && isLazyMode) {
          state.pagination.activePage = requestPage
        }
      } catch {
        if (!append) {
          state.tableRows = []
          state.pagination = {
            ...state.pagination,
            total: 0,
            pages: 0,
            from: 0,
            to: 0
          }
        }
      } finally {
        state.isLoading = false
        state.isLazyAppendInFlight = false
        await nextTick()
        setupLazyObserver()
      }
    }

    const {
      lazySentinelRef,
      canLoadMoreLazy,
      setupLazyObserver,
      teardownLazyObserver
    } = useBehaviourTableLazyScroll({
      state,
      getVisibleCount: () => state.tableRows.length,
      onAppend: () => fetchStudentList({ append: true })
    })

    const onSearchInput = (value) => {
      state.searchInput = value || ""
      if (searchDebounceId) {
        clearTimeout(searchDebounceId)
      }
      searchDebounceId = setTimeout(() => {
        state.pagination.activePage = 1
        fetchStudentList()
      }, SEARCH_DEBOUNCE_MS)
    }

    const onGradYearChange = () => {
      state.pagination.activePage = 1
      fetchStudentList()
    }

    const hydrateGradYearsForFilter = () => {
      const cached = store.getters["passBlocks/gradeYears"]
      if (cached?.length) {
        state.gradYears = cached
        return
      }

      store
        .dispatch("passBlocks/getGradeYears")
        .then((result) => {
          state.gradYears = result?.data?.data || []
        })
        .catch(() => {
          state.gradYears = []
        })
    }

    const resetFilters = () => {
      if (state.isLoading) return
      state.searchInput = ""
      state.filterParams.byGradYear = []
      state.sortColumn = null
      state.sortOrder = null
      state.pagination.activePage = 1
      if (searchDebounceId) {
        clearTimeout(searchDebounceId)
        searchDebounceId = null
      }
      fetchStudentList()
    }

    const onSortColumnChange = (sorterState) => {
      const column = String(sorterState?.column ?? "").trim()
      if (!column) {
        state.sortColumn = null
        state.sortOrder = null
      } else if (mapStudentListSortKey(column)) {
        state.sortColumn = column
        state.sortOrder = sorterState?.asc ? "asc" : "desc"
      } else {
        return
      }
      state.pagination.activePage = 1
      fetchStudentList()
    }

    const setPerPage = (option) => {
      if (!option) return
      state.pagination.per_page = option
      state.pagination.activePage = 1
      fetchStudentList()
    }

    const setActivePage = (page) => {
      state.pagination.activePage = page
      fetchStudentList()
    }

    const openStudentPointHistory = (row) => {
      if (!row?.studentId) return
      emit("select-student", { ...row })
    }

    const buildExportQuery = () => buildListQuery()

    const downloadStudentList = async () => {
      if (state.isExportLoading || state.isLoading) return

      try {
        state.isExportLoading = true
        const exportResult = await store.dispatch(
          "behaviourManage/exportStudentList",
          buildExportQuery()
        )
        if (exportResult?.url) {
          await download.downloadFromUrl(
            exportResult.url,
            exportResult.fileName || "student-list"
          )
        } else if (exportResult?.csvContent) {
          download.CSVExport(exportResult.csvContent, "student-list")
        }
      } catch {
        // Export errors are surfaced by the global axios handler when applicable.
      } finally {
        state.isExportLoading = false
      }
    }

    watch(
      () => props.selectedStudent,
      (student, previous) => {
        if (previous && !student) {
          fetchStudentList()
        }
      }
    )

    usePbisPointsSocketRefresh({
      refresh: fetchStudentList,
      enabled: computed(() => !props.selectedStudent)
    })

    onMounted(async () => {
      hydrateGradYearsForFilter()
      await fetchStudentList()
    })

    onUnmounted(() => {
      teardownLazyObserver()
      if (searchDebounceId) {
        clearTimeout(searchDebounceId)
        searchDebounceId = null
      }
    })

    return {
      behaviourTerminology,
      state,
      tableFields,
      paginationDisplay,
      hasActiveFilters,
      noItemsView,
      formatGradYear,
      onSearchInput,
      onGradYearChange,
      resetFilters,
      onSortColumnChange,
      sorterValue,
      lazySentinelRef,
      canLoadMoreLazy,
      setPerPage,
      setActivePage,
      openStudentPointHistory,
      downloadStudentList
    }
  }
}
</script>
